import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form, ErrorMessage } from 'formik';
import { AuthSocial } from '../AuthorizationSocial/AuthorizationSocial';
import { register, logIn } from '../../../redux/auth/authOperations';
import * as yup from 'yup';
import Notiflix from 'notiflix';
import Icons from 'images/allSvgFinTrack.svg';
import {
  BtnWrap,
  Custom,
  FormContainer,
  FormText,
  Input,
  Label,
  LinkPass,
  Pass,
  PrivacyText,
  PrivacyLink,
  PrivacyWrap,
  IconWrap,
  PasswordWrap,
  Checkbox,
} from './Form.styled';
import StyledButton from 'components/Buttons/Buttons.styled';
import { ModalPassReset } from 'components/Modal/ModalPassReset/ModalPassReset';

import { Loader } from 'components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import { ModalPassRecovery } from 'components/Modal/ModalPassRecovery/ModalPassRecovery';

const initialValues = {
  email: '',
  password: '',
};

export const AuthForm = ({ onConfirm, emailForResend, setEmailForResend }) => {
  const dispatch = useDispatch();
  const [button, setButton] = useState(null);
  const [isModalOpenPassReset, setIsModalOpenPassReset] = useState(false);
  const [isModalOpenPassRecovery, setIsModalOpenPassRecovery] = useState(false);
  const [isPasswordVisible, setPasswordVisibility] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState(false);
  const { t } = useTranslation();

  const shema = useMemo(
    () =>
      yup.object().shape({
        email: yup.string().required().email().max(255),
        password: yup.string().min(4).required(),
        checkbox: yup
          .boolean()
          .oneOf([true], t('notify.privacyNotConfirmed')),
      }),
    [t]
  );

  const handleClick = e => {
    switch (e.target.name) {
      case 'login':
        return setButton(true);
      case 'register':
        return setButton(false);

      default:
        return;
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    setIsLoading(true);
    try {
      if (!button) {
        if (isPrivacyPolicyAccepted) {
          const userData = {
            ...values,
            checked: isPrivacyPolicyAccepted,
          };
          await dispatch(register(userData));
        } else {
          Notiflix.Notify.warning(t('notify.privacyNotConfirmed'));
        }
      } else {
        const userData = {
          ...values,
          checked: isPrivacyPolicyAccepted,
        };
        await dispatch(logIn(userData));
        resetForm();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setIsModalOpenPassReset(true);
  };

  const handleModalClose = () => {
    setIsModalOpenPassReset(false);
  };

  const toggleCodeVisibility = () => {
    setPasswordVisibility(!isPasswordVisible);
  };

  const handleCheckboxChange = e => {
    setIsPrivacyPolicyAccepted(e.target.checked);
  };

  return (
    <FormContainer>
      {isLoading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={shema}
          onSubmit={handleSubmit}
        >
          <Form>
            <AuthSocial isLogin={button} />
            <FormText>{t('authForm.descFormAuth')}</FormText>
            <label htmlFor="email">
              <Label>{t('authForm.email')}:</Label>
              <Input
                type="email"
                name="email"
                placeholder={t('placeholder.email')}
                autoComplete="off"
              />
              <ErrorMessage name="email" component={Custom} />
            </label>
            <PasswordWrap>
              <Pass htmlFor="password">
                <Label>{t('authForm.password')}:</Label>
                <Input
                  type={isPasswordVisible ? 'text' : 'password'}
                  name="password"
                  placeholder={t('placeholder.password')}
                  autoComplete="off"
                />
                <IconWrap onClick={toggleCodeVisibility}>
                  {isPasswordVisible ? (
                    <svg
                      style={{
                        color: '#FF7601',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                      }}
                    >
                      <use href={`${Icons}#icon-eye`}></use>
                    </svg>
                  ) : (
                    <svg
                      style={{
                        color: '#FF7601',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                      }}
                    >
                      <use href={`${Icons}#icon-eye-crossed`}></use>
                    </svg>
                  )}
                </IconWrap>
                <ErrorMessage name="password" component={Custom} />
              </Pass>
            </PasswordWrap>
            <LinkPass onClick={handleForgotPasswordClick}>
              {t('authForm.forgotPassword')}?
            </LinkPass>
            {isModalOpenPassReset && (
              <ModalPassReset
                onClose={handleModalClose}
                onConfirm={onConfirm}
                emailForResend={emailForResend}
                setEmailForResend={setEmailForResend}
                setIsModalOpenPassReset={setIsModalOpenPassReset}
                setIsModalOpenPassRecovery={setIsModalOpenPassRecovery}
              />
            )}
            {isModalOpenPassRecovery && (
              <ModalPassRecovery
                emailForResend={emailForResend}
                setIsModalOpenPassRecovery={setIsModalOpenPassRecovery}
              />
            )}
            <PrivacyWrap>
              <Checkbox
                type="checkbox"
                name="checkbox"
                id="myCheckbox"
                checked={isPrivacyPolicyAccepted}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="myCheckbox" />
              <PrivacyText>
                {t('authForm.privacyPolicy')}
                <PrivacyLink href="https://sites.google.com/view/privacypolicyforfintrack?usp=sharing">
                  {t('authForm.privacy')}
                </PrivacyLink>
              </PrivacyText>
            </PrivacyWrap>
            <BtnWrap>
              <StyledButton onClick={handleClick} name="login" type="orange">
                {t('authForm.buttonLogIn')}
              </StyledButton>
              <StyledButton onClick={handleClick} name="register" type="white">
                {t('authForm.buttonRegistration')}
              </StyledButton>
            </BtnWrap>
          </Form>
        </Formik>
      )}
    </FormContainer>
  );
};
